"use client"

import * as React from "react"
import type { LucideIcon } from "lucide-react"
import { Boxes, CalendarDays, HandHeart, Landmark, PiggyBank, User, UserCog, Users } from "lucide-react"

import type { InvestigationContext } from "@/features/finance/lib/finance-investigation-url"
import { DimensionPicker, type DimensionPick } from "@/features/finance/containers/audit/launcher/dimension-picker"
import { Skeleton, cn } from "@ibada/ui"

import { LookupType } from "@/shared/model/lookup-types"
import { useTranslations } from "@/i18n"

/** The launcher's start dimensions, in display order. */
export enum StartDimension {
  Account = "account",
  Fund = "fund",
  Activity = "activity",
  Department = "department",
  Ministry = "ministry",
  Group = "group",
  Member = "member",
  User = "user",
}

type StartDimensionSpec = {
  dimension: StartDimension
  lookup: LookupType
  icon: LucideIcon
  /**
   * A parish transaction carries account, fund and activity and nothing else (see parish-finance.schemas: no
   * memberId, groupId, departmentId or ministryId), so at parish scope these can never match anything. Offering
   * them would promise an investigation that always comes back empty, which reads as "no findings" rather than
   * "not applicable".
   */
  branchOnly: boolean
  toContext: (pick: DimensionPick) => InvestigationContext
}

export const START_DIMENSIONS: StartDimensionSpec[] = [
  { dimension: StartDimension.Account, lookup: LookupType.AccountingAccounts, icon: Landmark, branchOnly: false, toContext: (p) => ({ accountingAccountId: p.id, label: p.label }) },
  { dimension: StartDimension.Fund, lookup: LookupType.Funds, icon: PiggyBank, branchOnly: false, toContext: (p) => ({ fundId: p.id, label: p.label }) },
  { dimension: StartDimension.Activity, lookup: LookupType.Activities, icon: CalendarDays, branchOnly: false, toContext: (p) => ({ activityId: p.id, label: p.label }) },
  { dimension: StartDimension.Department, lookup: LookupType.Departments, icon: Boxes, branchOnly: true, toContext: (p) => ({ departmentId: p.id, label: p.label }) },
  { dimension: StartDimension.Ministry, lookup: LookupType.Ministries, icon: HandHeart, branchOnly: true, toContext: (p) => ({ ministryId: p.id, label: p.label }) },
  { dimension: StartDimension.Group, lookup: LookupType.Groups, icon: Users, branchOnly: true, toContext: (p) => ({ groupId: p.id, label: p.label }) },
  { dimension: StartDimension.Member, lookup: LookupType.Members, icon: User, branchOnly: true, toContext: (p) => ({ memberId: p.id, label: p.label }) },
  { dimension: StartDimension.User, lookup: LookupType.Users, icon: UserCog, branchOnly: false, toContext: (p) => ({ actorUserId: p.id, label: p.label }) },
]

export function startDimensionsFor(parishContext: boolean): StartDimensionSpec[] {
  return parishContext ? START_DIMENSIONS.filter((spec) => !spec.branchOnly) : START_DIMENSIONS
}

type StartDimensionPickersProps = {
  workspaceId?: string
  branchId: string | null
  parishContext: boolean
  onInvestigate: (context: InvestigationContext) => void
  mobile?: boolean
}

/** The dimension pickers themselves, shared by the desktop card and the phone list. */
export function StartDimensionPickers({ workspaceId, branchId, parishContext, onInvestigate, mobile = false }: StartDimensionPickersProps) {
  const t = useTranslations("finance.audit.launcher")
  return (
    <>
      {startDimensionsFor(parishContext).map((spec) => {
        const Icon = spec.icon
        return (
          <DimensionPicker
            key={spec.dimension}
            type={spec.lookup}
            label={t(`dimensions.${spec.dimension}` as never)}
            icon={<Icon aria-hidden />}
            workspaceId={workspaceId}
            branchId={branchId}
            parishContext={parishContext}
            onPick={(pick) => onInvestigate(spec.toContext(pick))}
            mobile={mobile}
            doneLabel={t("mobile.done")}
            emptyLabel={t("mobile.noResults")}
          />
        )
      })}
    </>
  )
}

/** "Partir de…": every way to start an investigation from one record, as compact pickers. */
export function StartFromCard(props: Omit<StartDimensionPickersProps, "mobile">) {
  const t = useTranslations("finance.audit.launcher")
  return (
    <section
      data-testid='audit-start-from'
      className='flex flex-col gap-2.5 rounded-2xl border border-border/60 bg-card px-[18px] py-4'
    >
      <h2 className='text-sm font-bold text-foreground'>{t("startBy")}</h2>
      <div className='grid grid-cols-2 gap-2'>
        <StartDimensionPickers {...props} />
      </div>
    </section>
  )
}

/** A small tile for one correction signal (reversed / voided): its live count, opening the Explorer on its facet. */
export function SignalCard({
  icon,
  count,
  label,
  toneClassName,
  onClick,
  testId,
}: {
  icon: React.ReactNode
  count: number | null
  label: string
  toneClassName: string
  onClick: () => void
  testId?: string
}) {
  return (
    <button
      type='button'
      data-testid={testId}
      onClick={onClick}
      className='flex items-center gap-3 rounded-2xl border border-border/60 bg-card px-[18px] py-4 text-left transition-colors hover:border-primary/30 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none'
    >
      <span aria-hidden className={cn("flex size-[38px] shrink-0 items-center justify-center rounded-[11px] [&_svg]:size-[18px]", toneClassName)}>
        {icon}
      </span>
      <span className='flex min-w-0 flex-col'>
        {count === null ? (
          // Still counting: a placeholder the size of the figure, never a zero.
          <Skeleton aria-hidden className='my-0.5 h-6 w-10 rounded-lg' />
        ) : (
          <span className='text-xl leading-tight font-extrabold tabular-nums'>{count}</span>
        )}
        <span className='truncate text-[12.5px] text-muted-foreground'>{label}</span>
      </span>
    </button>
  )
}
